import { useEffect, useRef, useState, useCallback } from 'react';
import { useSharedWS } from './useSocket';

interface EditorUser {
    id: string;
    name: string;
    color: string;
}

export interface CursorPosition {
    x: number;
    y: number;
    userId: string;
    user: EditorUser;
}

export interface DocumentPatch {
    delta: unknown;
    userId: string;
}

interface EditorSocketState {
    isConnected: boolean;
    isReconnecting: boolean;
    isOffline: boolean;
    connectedUsers: EditorUser[];
    cursors: CursorPosition[];
    reconnectAttempts: number;
    queuedMessages: number;
}

interface MockSocket {
    userId: string;
    userName: string;
    userColor: string;
    sessionId?: string;
    onMessage?: (message: unknown) => void;
    onCursorUpdate?: (cursor: CursorPosition) => void;
    onDocumentPatch?: (patch: DocumentPatch) => void;
    onUserJoined?: (user: EditorUser) => void;
    onUserLeft?: (userId: string) => void;
    send: (message: unknown) => void;
}

interface MockMessage {
    type: string;
    delta?: unknown;
    userId?: string;
    x?: number;
    y?: number;
    user?: EditorUser;
}

interface UseEditorSocketOptions {
    onMessage?: (message: unknown) => void;
    onCursorUpdate?: (cursor: CursorPosition) => void;
    onDocumentPatch?: (patch: DocumentPatch) => void;
    onUserJoined?: (user: EditorUser) => void;
    onUserLeft?: (userId: string) => void;
    onBeeLogChunk?: (chunk: any) => void;
    onConnectionChange?: (isConnected: boolean) => void;
    onOpen?: () => void;
    onError?: () => void;
    userId?: string;
    userName?: string;
    userColor?: string;
    sessionId?: string;
    abdaMode?: boolean;
}

export function useEditorSocket(options: UseEditorSocketOptions = {}) {
    const messageQueueRef = useRef<unknown[]>([]);
    const [state, setState] = useState<EditorSocketState>({
        isConnected: false,
        isReconnecting: false,
        isOffline: false,
        connectedUsers: [],
        cursors: [],
        reconnectAttempts: 0,
        queuedMessages: 0,
    });

    const [status, setStatus] = useState<'idle' | 'connecting' | 'open' | 'error' | 'disabled'>(
        'connecting'
    );
    const isTestEnvRef = useRef(false);

    const {
        onMessage,
        onCursorUpdate,
        onDocumentPatch,
        onUserJoined,
        onUserLeft,
        onBeeLogChunk,
        onConnectionChange,
        onOpen,
        onError,
        userId = `user-${Math.random().toString(36).substr(2, 9)}`,
        userName = `User ${userId.slice(-4)}`,
        userColor = '#3B82F6',
        sessionId,
        abdaMode = false,
    } = options;

    const onMessageRef = useRef(onMessage);
    const onCursorUpdateRef = useRef(onCursorUpdate);
    const onDocumentPatchRef = useRef(onDocumentPatch);
    const onUserJoinedRef = useRef(onUserJoined);
    const onUserLeftRef = useRef(onUserLeft);
    const onBeeLogChunkRef = useRef(onBeeLogChunk);
    const onConnectionChangeRef = useRef(onConnectionChange);
    const onOpenRef = useRef(onOpen);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onMessageRef.current = onMessage;
        onCursorUpdateRef.current = onCursorUpdate;
        onDocumentPatchRef.current = onDocumentPatch;
        onUserJoinedRef.current = onUserJoined;
        onUserLeftRef.current = onUserLeft;
        onBeeLogChunkRef.current = onBeeLogChunk;
        onConnectionChangeRef.current = onConnectionChange;
        onOpenRef.current = onOpen;
        onErrorRef.current = onError;
    });

    useEffect(() => {
        const isTestEnvironment =
            typeof window !== 'undefined' &&
            (navigator.userAgent.includes('Playwright') ||
                navigator.userAgent.includes('HeadlessChrome') ||
                import.meta.env.MODE === 'test');
        isTestEnvRef.current = isTestEnvironment || abdaMode;

        if (isTestEnvironment || abdaMode) {
            if (abdaMode) {
                console.debug(
                    '[EditorSocket] ABDA 2.0 mode detected, skipping WebSocket connection'
                );
                setStatus('disabled');
                setState(prev => ({
                    ...prev,
                    isConnected: false,
                    isReconnecting: false,
                    isOffline: false,
                    reconnectAttempts: 0,
                }));
            } else {
                console.debug('[EditorSocket] Test environment detected, using mock connection');
                setStatus('open');
                setState(prev => ({
                    ...prev,
                    isConnected: true,
                    isReconnecting: false,
                    isOffline: false,
                    reconnectAttempts: 0,
                }));
            }

            if (typeof window !== 'undefined' && !abdaMode) {
                if (
                    !(window as unknown as { __mockEditorSockets?: MockSocket[] })
                        .__mockEditorSockets
                ) {
                    (
                        window as unknown as { __mockEditorSockets: MockSocket[] }
                    ).__mockEditorSockets = [];
                }

                const mockSocket = {
                    userId,
                    userName,
                    userColor,
                    sessionId,
                    onMessage: onMessageRef.current,
                    onCursorUpdate: onCursorUpdateRef.current,
                    onDocumentPatch: onDocumentPatchRef.current,
                    onUserJoined: onUserJoinedRef.current,
                    onUserLeft: onUserLeftRef.current,
                    send: (message: unknown) => {
                        const typedMessage = message as MockMessage;
                        console.log(
                            '[MockSocket] Broadcasting message:',
                            message,
                            'to',
                            (window as unknown as { __mockEditorSockets: MockSocket[] })
                                .__mockEditorSockets.length,
                            'sockets'
                        );
                        (
                            window as unknown as { __mockEditorSockets: MockSocket[] }
                        ).__mockEditorSockets.forEach((socket: MockSocket) => {
                            if (socket !== mockSocket) {
                                console.log('[MockSocket] Sending to socket:', socket.userId);
                                if (typedMessage.type === 'patch' && socket.onDocumentPatch) {
                                    socket.onDocumentPatch({
                                        delta: typedMessage.delta,
                                        userId: typedMessage.userId || userId,
                                    });
                                } else if (
                                    typedMessage.type === 'cursor' &&
                                    socket.onCursorUpdate
                                ) {
                                    socket.onCursorUpdate({
                                        x: typedMessage.x!,
                                        y: typedMessage.y!,
                                        userId: typedMessage.userId || userId,
                                        user: typedMessage.user!,
                                    });
                                } else if (socket.onMessage) {
                                    socket.onMessage(message);
                                }
                            }
                        });
                    },
                };

                (
                    window as unknown as { __mockEditorSockets: MockSocket[] }
                ).__mockEditorSockets.push(mockSocket);
                console.log(
                    '[MockSocket] Registered socket. Total:',
                    (window as unknown as { __mockEditorSockets: MockSocket[] }).__mockEditorSockets
                        .length
                );

                (window as unknown as { __mockEditorCleanup: () => void }).__mockEditorCleanup =
                    () => {
                        const index = (
                            window as unknown as { __mockEditorSockets: MockSocket[] }
                        ).__mockEditorSockets.indexOf(mockSocket);
                        if (index !== -1) {
                            (
                                window as unknown as { __mockEditorSockets: MockSocket[] }
                            ).__mockEditorSockets.splice(index, 1);
                            console.log(
                                '[MockSocket] Cleaned up socket. Remaining:',
                                (window as unknown as { __mockEditorSockets: MockSocket[] })
                                    .__mockEditorSockets.length
                            );
                        }
                    };

                setTimeout(() => {
                    (
                        window as unknown as { __mockEditorSockets: MockSocket[] }
                    ).__mockEditorSockets.forEach((socket: MockSocket) => {
                        if (socket !== mockSocket && socket.onUserJoined) {
                            socket.onUserJoined({ id: userId, name: userName, color: userColor });
                        }
                    });
                }, 100);
            }

            onConnectionChangeRef.current?.(true);
            onOpenRef.current?.();
        }
    }, [userId, userName, userColor, sessionId, abdaMode]);

    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            console.debug('[EditorSocket] onmessage', event.data);
            const message = JSON.parse(event.data);
            onMessageRef.current?.(message);

            switch (message.type) {
                case 'joined':
                    break;

                case 'user_joined':
                    setState(prev => ({
                        ...prev,
                        connectedUsers: [
                            ...prev.connectedUsers.filter(u => u.id !== message.user.id),
                            message.user,
                        ],
                    }));
                    onUserJoinedRef.current?.(message.user);
                    break;

                case 'user_left':
                    setState(prev => ({
                        ...prev,
                        connectedUsers: prev.connectedUsers.filter(u => u.id !== message.userId),
                        cursors: prev.cursors.filter(c => c.userId !== message.userId),
                    }));
                    onUserLeftRef.current?.(message.userId);
                    break;

                case 'cursor': {
                    const cursorUpdate: CursorPosition = {
                        x: message.x,
                        y: message.y,
                        userId: message.userId,
                        user: message.user,
                    };
                    setState(prev => ({
                        ...prev,
                        cursors: [
                            ...prev.cursors.filter(c => c.userId !== message.userId),
                            cursorUpdate,
                        ],
                    }));
                    onCursorUpdateRef.current?.(cursorUpdate);
                    break;
                }

                case 'patch': {
                    const patch: DocumentPatch = {
                        delta: message.delta,
                        userId: message.userId,
                    };
                    onDocumentPatchRef.current?.(patch);
                    break;
                }

                case 'beeLogChunk':
                    onBeeLogChunkRef.current?.(message.chunk);
                    break;

                case 'pong':
                    break;

                default:
                    console.warn('Unknown editor message type:', message.type);
            }
        } catch (error) {
            console.error('Error parsing editor message:', error);
        }
    }, []);

    const handleReconnect = useCallback(() => {
        console.debug('[EditorSocket] Reconnected, resending join payload');
        send({
            type: 'join',
            userId,
            name: userName,
            color: userColor,
        });

        setState(prev => ({
            ...prev,
            isConnected: true,
            isReconnecting: false,
            reconnectAttempts: 0,
        }));

        onConnectionChangeRef.current?.(true);
        onOpenRef.current?.();
    }, [userId, userName, userColor]);

    const { send, socket } = useSharedWS('/editor', handleMessage, handleReconnect, {
        enabled: !abdaMode,
    });

    useEffect(() => {
        if (isTestEnvRef.current) return;

        if (socket?.readyState === WebSocket.OPEN) {
            console.debug('[EditorSocket] WebSocket connection established');
            setStatus('open');
            setState(prev => ({
                ...prev,
                isConnected: true,
                isReconnecting: false,
                isOffline: false,
                reconnectAttempts: 0,
                queuedMessages: 0,
            }));

            if (typeof window !== 'undefined' && (window as any).beeMemory) {
                (window as any).beeMemory.set(
                    `websocket:status:${userId}`,
                    JSON.stringify({
                        status: 'connected',
                        timestamp: Date.now(),
                        sessionId,
                        reconnectAttempts: 0,
                    })
                );
            }

            while (messageQueueRef.current.length > 0) {
                const queuedMessage = messageQueueRef.current.shift();
                send(queuedMessage);
            }

            send({
                type: 'join',
                userId,
                name: userName,
                color: userColor,
            });

            onConnectionChangeRef.current?.(true);
            onOpenRef.current?.();
        } else if (socket?.readyState === WebSocket.CONNECTING) {
            setStatus('connecting');
        } else {
            setStatus('error');
            setState(prev => ({
                ...prev,
                isConnected: false,
            }));
            onConnectionChangeRef.current?.(false);
            onErrorRef.current?.();

            if (typeof window !== 'undefined' && (window as any).beeMemory) {
                (window as any).beeMemory.set(
                    `websocket:status:${userId}`,
                    JSON.stringify({
                        status: 'disconnected',
                        timestamp: Date.now(),
                        sessionId,
                        reconnectAttempts: state.reconnectAttempts + 1,
                    })
                );
            }
        }
    }, [socket, send, userId, userName, userColor, sessionId, state.reconnectAttempts]);

    const sendMessage = useCallback(
        (message: unknown) => {
            if (isTestEnvRef.current && typeof window !== 'undefined') {
                const mockSockets =
                    (window as unknown as { __mockEditorSockets?: MockSocket[] })
                        .__mockEditorSockets || [];
                const ourSocket = mockSockets.find((s: MockSocket) => s.userId === userId);
                if (ourSocket) {
                    ourSocket.send(message);
                }
            } else if (socket?.readyState === WebSocket.OPEN) {
                send(message);
                while (messageQueueRef.current.length > 0) {
                    const queuedMessage = messageQueueRef.current.shift();
                    send(queuedMessage);
                }
                setState(prev => ({ ...prev, queuedMessages: 0 }));
            } else {
                messageQueueRef.current.push(message);
                setState(prev => ({ ...prev, queuedMessages: messageQueueRef.current.length }));
            }
        },
        [userId, socket, send]
    );

    const sendCursor = useCallback(
        (x: number, y: number) => {
            sendMessage({
                type: 'cursor',
                x,
                y,
                user: { id: userId, name: userName, color: userColor },
            });
        },
        [sendMessage, userId, userName, userColor]
    );

    const sendPatch = useCallback(
        (delta: unknown) => {
            sendMessage({
                type: 'patch',
                delta,
            });
        },
        [sendMessage]
    );

    const reconnect = useCallback(() => {
        setState(prev => ({
            ...prev,
            isOffline: false,
            isReconnecting: false,
            reconnectAttempts: 0,
        }));
    }, []);

    const disconnect = useCallback(() => {
        if (isTestEnvRef.current && typeof window !== 'undefined') {
            if ((window as unknown as { __mockEditorCleanup?: () => void }).__mockEditorCleanup) {
                (window as unknown as { __mockEditorCleanup: () => void }).__mockEditorCleanup();
            }
        }

        if (socket?.readyState === WebSocket.OPEN) {
            send({
                type: 'leave',
                userId,
            });
        }

        setState({
            isConnected: false,
            isReconnecting: false,
            isOffline: false,
            connectedUsers: [],
            cursors: [],
            reconnectAttempts: 0,
            queuedMessages: 0,
        });
    }, [userId, socket, send]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (socket && socket.readyState === WebSocket.OPEN) {
                send({
                    type: 'leave',
                    userId,
                });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            disconnect();
        };
    }, [disconnect, userId, socket, send]);

    return {
        ...state,
        sendMessage,
        sendCursor,
        sendPatch,
        reconnect,
        disconnect,
        ws: socket,
        status,
    };
}
